import { Command } from "cmdk";
import { CalendarDays, FileText, ListTodo, Plus, Settings, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateNote, useNotes } from "@/lib/queries";

/**
 * ⌘K palette — the "fast to reach" primitive.
 *
 * Search runs server-side against the same full-text index the notes list uses,
 * so the palette finds text in note *bodies*, not just titles.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const create = useCreateNote();
  const { data } = useNotes(query.trim() ? { q: query.trim() } : {});

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      // ⌘N is quick capture: straight into a new note, no dialog.
      if (mod && e.key.toLowerCase() === "n") {
        e.preventDefault();
        create.mutate({}, { onSuccess: (r) => navigate(`/notes/${r.note.id}`) });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [create, navigate]);

  const go = (to: string) => {
    setOpen(false);
    setQuery("");
    navigate(to);
  };

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Command palette"
      shouldFilter={false}
      className="fixed inset-0 z-50 grid place-items-start justify-center bg-ink/25 pt-[12vh] backdrop-blur-sm"
    >
      <div className="w-[min(92vw,34rem)] overflow-hidden rounded-card border border-line bg-raised shadow-card">
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder="Search notes, or jump to…"
          className="w-full border-b border-line bg-transparent px-5 py-4 text-sm text-ink placeholder:text-ink-faint focus:outline-none"
        />
        <Command.List className="max-h-[min(24rem,50vh)] overflow-y-auto p-2">
          <Command.Empty className="px-3 py-6 text-center text-sm text-ink-faint">
            Nothing found.
          </Command.Empty>

          <Command.Group heading="Actions" className="px-1 text-xs text-ink-faint">
            <Item
              icon={<Plus className="h-4 w-4" />}
              onSelect={() => {
                setOpen(false);
                create.mutate({}, { onSuccess: (r) => navigate(`/notes/${r.note.id}`) });
              }}
            >
              New note <Hint>⌘N</Hint>
            </Item>
          </Command.Group>

          {(data?.notes ?? []).length > 0 ? (
            <Command.Group heading="Notes" className="px-1 text-xs text-ink-faint">
              {data!.notes.slice(0, 8).map((n) => (
                <Item key={n.id} icon={<FileText className="h-4 w-4" />} onSelect={() => go(`/notes/${n.id}`)}>
                  <span className="truncate">{n.title || "Untitled"}</span>
                </Item>
              ))}
            </Command.Group>
          ) : null}

          <Command.Group heading="Go to" className="px-1 text-xs text-ink-faint">
            <Item icon={<ListTodo className="h-4 w-4" />} onSelect={() => go("/tasks")}>Tasks</Item>
            <Item icon={<CalendarDays className="h-4 w-4" />} onSelect={() => go("/calendar")}>Calendar</Item>
            <Item icon={<Users className="h-4 w-4" />} onSelect={() => go("/teams")}>Teams</Item>
            <Item icon={<Settings className="h-4 w-4" />} onSelect={() => go("/settings")}>Settings</Item>
          </Command.Group>
        </Command.List>
      </div>
    </Command.Dialog>
  );
}

function Item({
  icon,
  children,
  onSelect,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-3 rounded-2xl px-3 py-2.5 text-sm text-ink data-[selected=true]:bg-sunken"
    >
      <span className="text-ink-muted">{icon}</span>
      <span className="flex flex-1 items-center gap-2 truncate">{children}</span>
    </Command.Item>
  );
}

const Hint = ({ children }: { children: React.ReactNode }) => (
  <kbd className="ml-auto rounded-md bg-sunken px-1.5 py-0.5 font-sans text-[0.65rem] text-ink-faint">
    {children}
  </kbd>
);
